import { Timestamp } from "firebase/firestore";
import { DateTime, DateTimeUnit, DurationUnit, Interval } from "luxon";
import { formatTimestamp } from "./dateHandler.util";

export function timeAgo(date: Date): string {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();

  const secondInMs = 1000;
  const minuteInMs = secondInMs * 60;
  const hourInMs = minuteInMs * 60;
  const dayInMs = hourInMs * 24;
  const yearInMs = dayInMs * 365;

  let duration: number;
  let unit: string;

  if (diffInMs < minuteInMs) {
    duration = Math.round(diffInMs / secondInMs);
    unit = "s";
  } else if (diffInMs < hourInMs) {
    duration = Math.round(diffInMs / minuteInMs);
    unit = "m";
  } else if (diffInMs < dayInMs) {
    duration = Math.round(diffInMs / hourInMs);
    unit = "h";
  } else if (diffInMs < yearInMs) {
    duration = Math.round(diffInMs / dayInMs);
    unit = "d";
  } else {
    duration = Math.round(diffInMs / yearInMs);
    unit = "y";
  }

  return `${duration}${unit} ago`;
}
export function dateToHH_MM(date: Date): string {
  const hours = date.getHours();
  const minutes = date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes();
  const amPm = hours >= 12 ? "pm" : "am";

  // Adjust based on your desired time format (e.g., include seconds)
  const formattedTime = `${hours % 12}:${minutes}${amPm}`;

  return formattedTime;
}

function getWeek(date: Date): number {
  const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
  const daysSinceFirstDay = Math.floor((date.getTime() - firstDayOfYear.getTime()) / (1000 * 60 * 60 * 24));
  return Math.ceil((firstDayOfYear.getDay() + 1 + daysSinceFirstDay) / 7);
}

export function formatDateLabel(date: Date): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dateCopy = new Date(date);
  dateCopy.setHours(0, 0, 0, 0);

  // Calculate difference in days (positive means date is in the past)
  const diffInDays = Math.floor((today.getTime() - dateCopy.getTime()) / (1000 * 60 * 60 * 24));

  // Check for today, yesterday, or current week
  if (diffInDays === 0) {
    return "Today";
  } else if (diffInDays === 1) {
    return "Yesterday";
  } else if (diffInDays >= 2 && diffInDays <= 6 && getWeek(today) === getWeek(dateCopy)) {
    const weekday = dateCopy.toLocaleDateString("en-US", { weekday: "short" });
    return weekday;
  } else {
    // Fallback for non-current week dates or older dates
    return dateCopy.toLocaleDateString("en-US", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
    });
  }
}

export function getOldestByDate<T extends {}, K extends keyof T>(data: T[], dateKey: K): T | undefined {
  if (!data || data.length === 0) {
    return undefined;
  }

  // Initialize with the first element
  let oldestItem = data[0];

  // Iterate through the array and compare dates
  for (let i = 1; i < data.length; i++) {
    if (data[i][dateKey] < oldestItem[dateKey]) {
      oldestItem = data[i];
    }
  }

  return oldestItem;
}

export const getStartOfYear = (year: string): Date => {
  return new Date(Number(year), 0, 1, 0, 0, 0, 0); // January 1st, at 00:00:00
};

export function convertLocalDateToTimestamp(inputDate?: string): Timestamp | undefined {
  if (!inputDate) return undefined;

  const dt = DateTime.fromISO(inputDate, { zone: "local" }).startOf("day");
  return Timestamp.now();
}

export function convertLocalDateToJsDate(inputDate?: string): Date | undefined {
  if (!inputDate) return undefined;

  return DateTime.fromISO(inputDate, { zone: "local" }).startOf("day").toJSDate();
}

export function formatTimestampWithRelativeDate(timestamp: any): string {
  if (!timestamp) return "N/A";

  const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
  const dateString = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const time = dateToHH_MM(date);

  return `${dateString} at ${time}`;
}

export class DateUtils {
  /**
   * Converts various date types to DateTime instance.
   */
  private static toDateTime(t: Timestamp | Date | DateTime | string): DateTime {
    if (t instanceof DateTime) return t;
    if (t instanceof Date) return DateTime.fromJSDate(t);

    // Check for string (ISO format)
    if (typeof t === "string") {
      return DateTime.fromISO(t);
    }

    // Check for Firebase Timestamp
    if (t instanceof Timestamp) {
      return DateTime.fromJSDate(t.toDate());
    }

    // Fallback check for Timestamp-like objects (has toDate method)
    if (t && typeof (t as any).toDate === "function") {
      return DateTime.fromJSDate((t as Timestamp).toDate());
    }

    throw new Error("Unsupported date type");
  }

  /**
   * Checks if the first date is before (expired relative to) the second date.
   */
  public static checkIsExpiredDate(
    t1: Timestamp | Date | DateTime | string,
    t2: Timestamp | Date | DateTime | string
  ): boolean {
    const dt1 = this.toDateTime(t1);
    const dt2 = this.toDateTime(t2);
    return dt1.toMillis() < dt2.toMillis();
  }

  /**
   * Adds specified amount of time to a DateTime.
   */
  public static add(value: DateTime | string | Date | Timestamp, amount: number, unit: DurationUnit): DateTime {
    let dt: DateTime;

    if (value instanceof DateTime) {
      dt = value;
    } else if (typeof value === "string") {
      dt = DateTime.fromISO(value);
    } else if (value instanceof Date) {
      dt = DateTime.fromJSDate(value);
    } else if (value instanceof Timestamp) {
      dt = DateTime.fromJSDate(value.toDate());
    } else if (value && typeof (value as any).toDate === "function") {
      // Fallback for Timestamp-like objects
      dt = DateTime.fromJSDate((value as Timestamp).toDate());
    } else {
      throw new Error("Unsupported date type for add operation");
    }

    return dt.plus({ [unit]: amount });
  }

  /**
   * Subtracts specified amount of time from a DateTime.
   */
  public static subtract(value: DateTime | string | Date | Timestamp, amount: number, unit: DurationUnit): DateTime {
    let dt: DateTime;

    if (value instanceof DateTime) {
      dt = value;
    } else if (typeof value === "string") {
      dt = DateTime.fromISO(value);
    } else if (value instanceof Date) {
      dt = DateTime.fromJSDate(value);
    } else if (value instanceof Timestamp) {
      dt = DateTime.fromJSDate(value.toDate());
    } else if (value && typeof (value as any).toDate === "function") {
      // Fallback for Timestamp-like objects
      dt = DateTime.fromJSDate((value as Timestamp).toDate());
    } else {
      throw new Error("Unsupported date type for subtract operation");
    }

    return dt.minus({ [unit]: amount });
  }

  /**
   * Returns current time as Firebase Timestamp.
   */
  public static now(): Timestamp {
    return Timestamp.now();
  }

  /**
   * Converts DateTime to Firebase Timestamp.
   */
  public static toTimestamp(dt: DateTime): Timestamp {
    return Timestamp.fromDate(dt.toJSDate());
  }

  /**
   * Converts various date types to Firebase Timestamp.
   */
  public static convertToTimestamp(value: DateTime | string | Date | Timestamp): Timestamp {
    if (value instanceof Timestamp) {
      return value;
    }

    const dt = this.toDateTime(value);
    return this.toTimestamp(dt);
  }
}

export const getTimeAgo = (timestamp: Timestamp) => {
  const now = Date.now();
  const postTime = timestamp.seconds * 1000;
  const diffInHours = Math.floor((now - postTime) / (1000 * 60 * 60));

  if (diffInHours < 1) return "Just now";
  if (diffInHours < 24) return `${diffInHours}h ago`;
  if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
  return formatTimestamp(timestamp);
};
